<!-- Copyright (c) 2024 Digital Systems (M) Sdn. Bhd. -->

<template>
  <div class="check-box-set-main">
    <!-- BoxContainer doesn't support raw html in the title, which is needed to set the text colour of the required indicator -->
    <!-- draw own div instead of using BoxContainer -->
    <div v-if="showBox" class="check-boxes-with-box">
      <div class="title">
        <span v-if="isRequired" class="is-required-indicator">**</span>
        <span>{{title}}</span>
      </div>
      <div :class="checkBoxesContainerClasses" id="boxed-check-boxes" @focusin="checkBoxesContainerFocusedIn" @click="checkBoxesContainerClicked">
        <CheckBoxV2 v-for="(checkBox, checkBoxIndex) in pCheckBoxes" :key="checkBoxIndex" :title="checkBox[titleKey]" :isChecked.sync="checkBox.isChecked" :isDisabled="isDisabled" @checkBoxClicked="checkBoxClicked(checkBox[titleKey], $event)" :ref="checkBox[titleKey]"/>
        <div v-if="errorMessage" class="error-message-container" :style="errorMessageContainerStyling">
          <span>{{errorMessage}}</span>
        </div>
      </div>
    </div>
    <div v-else class="check-boxes-without-box">
      <div class="title">
        <span v-if="isRequired" class="is-required-indicator">** </span>
        <span>{{title}}</span>
      </div>
      <div :class="checkBoxesContainerClasses" @focusin="checkBoxesContainerFocusedIn" @click="checkBoxesContainerClicked">
        <CheckBoxV2 v-for="(checkBox, checkBoxIndex) in pCheckBoxes" :key="checkBoxIndex" :title="checkBox[titleKey]" :isChecked.sync="checkBox.isChecked" :isDisabled="isDisabled" @checkBoxClicked="checkBoxClicked(checkBox[titleKey], $event)" :ref="checkBox[titleKey]"/>
        <div v-if="errorMessage" class="error-message-container" :style="errorMessageContainerStyling">
          <span>{{errorMessage}}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
export default {
  components: {
  },
  computed: {
    checkBoxesContainerClasses: function() {
      return [
        "check-boxes-container",
        this.isHorizontal ? "row-wise" : "column-wise",
      ];
    },
    errorMessageContainerStyling: function() {
      if (this.isHorizontal) {
        return { // only works if check boxes are lined up horizontally
          "text-overflow": "ellipsis", // because text overflow ellipsis needs white space nowrap to work https://developer.mozilla.org/en-US/docs/Web/CSS/text-overflow
          "white-space": "nowrap", // but white space nowrap forces the error message into a single line, you can't have ellipsis with multiple lines
        };
      } else { // webkit line clamp is needed if the ellipsis needs to be shown not on the first line, but at the end after a subsequent number of lines
        return { // https://stackoverflow.com/a/17029025 https://developer.mozilla.org/en-US/docs/Web/CSS/-webkit-line-clamp https://css-tricks.com/almanac/properties/l/line-clamp/
          "display": "-webkit-box", // webkit line clamp is supported across all major browsers, but as of writing this line clamp is being defined in the CSS standard
          "-webkit-box-orient": "vertical", // which will replace webkit line clamp in the future
          "-webkit-line-clamp": this.pCheckBoxes?.length || "1", // the number of lines needs to be specified, and needs to be greater than 0
        };
      }
    },
  },
  data: function() {
    return {
      pCheckBoxes: [],
    };
  },
  props: {
    title: { type: String, default: 'Check Boxes Title' }, // title of the check box set

    checkBoxes: { type: Array, default: function() { return [] } }, // array of objects to render into check boxes. use .sync
    // format: [
    //   {
    //     titleKey: "Check Box 1", // the key to be displayed as the check box title should be passed to the titleKey prop
    //     "isChecked": true, // indicates whether the check box is checked or unchecked
    //   },
    //   {
    //     titleKey: "Check Box 2",
    //     "isChecked": false,
    //   },
    // ]
    titleKey: { type: String, default: '' }, // The key of the object to show as the check box title
    isHorizontal: { type: Boolean, default: false }, // If true, the check boxes will appear in a row
    showBox: { type: Boolean, default: true }, // control whether the box around the check boxes are shown or hidden
    isRequired: { type: Boolean, default: false }, // controls whether the required asterisks are shown or hidden
    errorMessage: { type: String, default: '' }, // set an error message, use .sync
    isDisabled: { type: Boolean, default: false }, // to disable all check boxes and disallow selection

    // Public events
    // @checkBoxClicked(title, checked) - fired when a check box is checked or unchecked by the user, but not when changed programatically
  },
  methods: {
    updatePrivateCheckBoxes: function() {
      this.pCheckBoxes = structuredClone(this.checkBoxes);
    },
    checkBoxClicked: async function(title, checked) {
      await this.$nextTick(); // wait for private data member to update before updating prop, otherwise the previous value will be used to update the prop
      this.$emit("update:checkBoxes", this.pCheckBoxes);
      this.$emit("checkBoxClicked", title, checked);
    },
    checkBoxesContainerFocusedIn: function(focusEvent) { // clear error message if any of the check boxes have been focused on, including if it was focused on via keyboard
      this.$emit("update:errorMessage", ""); // specifically, listen to focusin instead of focus, because focusin bubbles up from the input to the div
    },
    checkBoxesContainerClicked: function(pointerEvent) { // clear error message if the container has been clicked on
      this.$emit("update:errorMessage", ""); // the container may be larger than the check boxes, and it's possible for the user to click on the container but not any of the check boxes
    },

    // Public Methods
    setFocus: function() { // focuses on the first check box in the array
      this.$refs[this.pCheckBoxes[0][this.titleKey]][0].setFocus();
      // because ref was used on a component with v-for, it returns an array that contains the component instance instead
      // thus the need to access the first element after specifying the ref

      this.$emit("update:errorMessage", "");
    },
  },
  mounted() {
    if (!this.checkBoxes || this.checkBoxes.length === 0) {
      if (process.env.NODE_ENV === "development") {
        console.error("CheckBoxSet - Check boxes not provided");
      }
      return;
    }

    if (this.titleKey === "") {
      if (process.env.NODE_ENV === "development") {
        console.error("CheckBoxSet - titleKey not provided");
      }
      return;
    }

    this.updatePrivateCheckBoxes();
  },
  watch: {
    checkBoxes: {
      handler: function(val) {
        this.updatePrivateCheckBoxes();
      },
      deep: true,
    },
  }
}
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';

.check-box-set-main {

  > .check-boxes-with-box {
    position: relative;
    border: 1px solid gray;
    margin-top: 8px;
    padding: 10px 5px 5px 5px;
    border-radius: 5px;

    > .title {
      position: absolute;
      top: -9px;
      left: 10px;
      padding: 0 10px;
      background-color: white;

      > .is-required-indicator {
        margin: 0 5px 0 -5px;
        color: red;
      }
    }
  }

  > .check-boxes-without-box {
    display: flex;
    flex-direction: column;

    .is-required-indicator {
      color: red;
    }
  }

  .check-boxes-container {
    display: flex;

    > .error-message-container {
      position: absolute;
      border-radius: 5px;
      padding: 1px 5px;
      max-height: calc(100% - 15px); // this doesn't work properly if no box is shown
      max-width: calc(100% - 10px); // the error message will grow past the component's boundaries
      background-color: red; // couldn't find a way to fix it, either show the box or use a shorter message
      color: white;
      overflow: hidden;
    }

    &:hover > .error-message-container {
      border: 1px solid black;
      max-height: unset;
      max-width: unset;
      -webkit-line-clamp: unset !important; // used when check boxes are aligned vertically
    }
  }

  .row-wise {
    flex-direction: row;
  }

  .column-wise {
    flex-direction: column;
  }

  #boxed-check-boxes { // offset by 2px from the top
    margin-top: 2px; // to prevent white background of the title from overlapping with the check boxes
  }
}

</style>